import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { MdDeleteOutline, MdModeEditOutline } from "react-icons/md";
import {
  Accordion,
  AccordionContent,
  AccordionTitle,
  Grid,
  Table,
  TableRow
} from "semantic-ui-react";
import { AccordionTitleView, CommonTable, CustomButton, DropDown } from "../../../components";
import { InputNumber } from "../../../components/InputText/InputNumber";
import { FacilityEnergyTable } from "../../../config/constants";
import { DEFAULT_ENARGY_SOURCE } from "../../../config/drawingConstants";
import "../facilityForms/facilityForm.scss";

const FacilityEnergySimulation = ({
  energyType,
  currentFacilityData,
  originalFacilityEnergyData,
  setDeleteData,
  setVisibleDeleteModal,
  renderTableData = false,
  isFacilitySimulation,
  isViewMode = false
}: any) => {
  const [accordionEnabled, setAccordionEnabled] = useState<boolean>(false);
  const [defaultEnergyType, setDefaultEnergyType] = useState<number>(DEFAULT_ENARGY_SOURCE);
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const {
    formState: { errors: errorsFacilityEnergy },
    handleSubmit: handleSubmitFacilityEnergy,
    setValue,
    reset,
    setError,
    control
  } = useForm();

  const handleClick = () => {
    setAccordionEnabled(!accordionEnabled);
  };

  const selectedEnergyType = useMemo(() => {
    return (
      Array.isArray(energyType) &&
      energyType?.find((item: any) => item?.value === String(defaultEnergyType))
    );
  }, [energyType, defaultEnergyType]);

  const addFacilityEnergyData = (data: any) => {
    const price = data?.Price ? Number(data?.Price) : 0;
    let updateData = {
      ...data,
      Price: price,
      kWhConsumed: Number(data?.kWhConsumed),
      Percentage: Number(data?.Percentage),
      EnergyType: Number(defaultEnergyType) || DEFAULT_ENARGY_SOURCE
    };

    if (enableEdit && editIndex !== null) {
      currentFacilityData[editIndex] = updateData;
    } else {
      currentFacilityData.push(updateData);
    }
    reset();
    setDefaultEnergyType(DEFAULT_ENARGY_SOURCE);
    setEditIndex(null);
    setEnableEdit(false);
  };

  const groupFacilityData = (data: any) => {
    let groupIngredientData = data?.reduce((currentType: any, data: any, index: any) => {
      data.index = index;
      let checkDataIsAvailable = currentType?.find((other: any) => other.key === data.Year);
      if (currentType?.length === 0) {
        let facilityData = {
          key: data.Year,
          value: [data]
        };
        return [...currentType, facilityData];
      } else if (checkDataIsAvailable) {
        let currentIndex = currentType?.findIndex((other: any) => other.key === data.Year);
        let updatedArray = [];

        const updateArray = [...currentType[currentIndex].value, data];
        const updatedObject = {
          ...currentType[currentIndex],
          value: updateArray
        };
        updatedArray = [
          ...currentType.slice(0, currentIndex),
          updatedObject,
          ...currentType.slice(currentIndex + 1)
        ];
        return updatedArray;
      } else {
        let facilityData = {
          key: data.Year,
          value: [data]
        };

        return [...currentType, facilityData];
      }
    }, []);
    return groupIngredientData;
  };
  const groupFacilityByYear = groupFacilityData(currentFacilityData);
  const originalGroupFacilityByYear = groupFacilityData(originalFacilityEnergyData);

  const checkValidData = (currentData: any) => {
    let totalOfPercentage = 0;

    const percentageValueData = groupFacilityByYear.find(
      (data: any) => data.key === currentData.Year
    );

    if (percentageValueData) {
      totalOfPercentage = percentageValueData.value.reduce((total: number, item: any) => {
        if (enableEdit && editIndex !== null && item.index === editIndex) {
          return total;
        }
        return total + Number(item.Percentage);
      }, 0);
      totalOfPercentage += Number(currentData.Percentage);
    }

    if (totalOfPercentage > 100) {
      setError("Percentage", {
        type: "manual",
        message: `Total of percentage in ${currentData.Year} is over 100`
      });
      return true;
    }

    return false;
  };

  const setFacilityEnergyDetails = (data: any) => {
    setValue("Percentage", data?.Percentage);
    setValue("Price", data?.Price);
    setValue("Year", data?.Year);
    setValue("kWhConsumed", data?.kWhConsumed);
    setDefaultEnergyType(data?.EnergyType);
    setEditIndex(data?.index);
    setEnableEdit(true);
  };

  const renderTableRow = (
    dataView: any,
    rowSpan: any,
    totalValue: any,
    onDelete: any,
    showIcons: boolean
  ) => {
    const energyTypeAsText =
      Array.isArray(energyType) &&
      energyType?.find((item: any) => item?.value === String(dataView?.EnergyType))?.text;
    return (
      <TableRow key={dataView?.id || dataView?.index}>
        {rowSpan > 0 && (
          <Table.Cell rowSpan={rowSpan}>
            <p>{dataView?.Year ?? ""}</p>
          </Table.Cell>
        )}
        <Table.Cell>
          <p>{energyTypeAsText ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{dataView?.kWhConsumed ?? ""} kWh</p>
        </Table.Cell>
        <Table.Cell>
          <p>{dataView?.Percentage ?? 0.0} %</p>
        </Table.Cell>
        <Table.Cell>
          <p>{dataView?.Price?.toFixed(2) ?? 0.0}</p>
        </Table.Cell>
        <Table.Cell>
          <p>{totalValue?.toFixed(2) ?? 0.0}</p>
        </Table.Cell>
        {!showIcons && !isViewMode ? (
          <Table.Cell>
            <Grid>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdModeEditOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={() => setFacilityEnergyDetails(dataView)}
                />
              </Grid.Column>
              <Grid.Column computer={6} tablet={16} mobile={16}>
                <MdDeleteOutline
                  cursor="pointer"
                  size={24}
                  color="var(--tableEditIcon)"
                  onClick={onDelete}
                />
              </Grid.Column>
            </Grid>
          </Table.Cell>
        ) : null}
      </TableRow>
    );
  };

  const loadOriginalTableData = () => {
    return originalGroupFacilityByYear?.map((groupData: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const kWhValue = dataView?.kWhConsumed || 0;
        const price = dataView?.Price || 0;
        const totalValue = kWhValue * price;

        return renderTableRow(dataView, isFirstRow ? rowCount : 0, totalValue, null, true);
      });
    });
  };

  const loadTableData = () => {
    return groupFacilityByYear?.map((groupData: any, groupIndex: any) => {
      const rowCount = groupData?.value?.length || 0;
      return groupData?.value?.map((dataView: any, dataIndex: any) => {
        const isFirstRow = dataIndex === 0;
        const kWhValue = dataView?.kWhConsumed || 0;
        const price = dataView?.Price || 0;
        const totalValue = kWhValue * price;

        const handleDelete = () => {
          setDeleteData(dataView);
          setVisibleDeleteModal(true);
        };

        return renderTableRow(
          dataView,
          isFirstRow ? rowCount : 0,
          totalValue,
          handleDelete,
          renderTableData
        );
      });
    });
  };

  return (
    <>
      <Accordion>
        <AccordionTitle active={accordionEnabled} onClick={handleClick}>
          <AccordionTitleView accordionEnabled={accordionEnabled} title={"Power Sources"} />
        </AccordionTitle>
        <AccordionContent active={accordionEnabled}>
          <Grid>
            <Grid.Row divided>
              {isFacilitySimulation ? (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  {originalFacilityEnergyData?.length > 0 && (
                    <CommonTable
                      tableHeaderData={FacilityEnergyTable.filter((e: any) => e.name !== "")}
                      isSimulateView
                    >
                      {loadOriginalTableData()}
                    </CommonTable>
                  )}
                </Grid.Column>
              ) : null}
              <Grid.Column
                computer={isFacilitySimulation ? 8 : 16}
                tablet={16}
                mobile={16}
                className={!renderTableData ? "userBankDetailsMain" : "recipeTableViewMain"}
              >
                <div>
                  {!renderTableData ? (
                    <Grid>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <DropDown
                          labelName={"Source*"}
                          placeholder="Source*"
                          currentData={energyType}
                          defaultValue={selectedEnergyType.value || null}
                          customGridColumn={"customGridColomnType"}
                          handleChangeState={(e: any, { value }: any) => {
                            setDefaultEnergyType(value);
                          }}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column
                        computer={4}
                        tablet={8}
                        mobile={16}
                        className="paddingRemoveBottom"
                      >
                        <InputNumber
                          control={control}
                          errors={errorsFacilityEnergy.Year}
                          labelName={"Year*"}
                          placeholder="Year"
                          name="Year"
                          required={true}
                          errorMessage="Year is required"
                          isYearInput={true}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={8} tablet={8} mobile={16} />
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityEnergy.kWhConsumed}
                          labelName={"kWh consumed*"}
                          placeholder="kWh consumed"
                          required={true}
                          name="kWhConsumed"
                          errorMessage="kWh consumed is required"
                          rightContentEnable={true}
                          rightContent={"kWh"}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityEnergy.Percentage}
                          labelName={"Percentage distribution*"}
                          placeholder="Percentage distribution"
                          name="Percentage"
                          required={true}
                          errorMessage="Percentage is required"
                          rightContentEnable={true}
                          rightContent={"%"}
                          otherValidation={{
                            validate: {
                              valueCheck: (v: any) =>
                                Number(v) <= 100 || "Percentage should be less than or equal to 100"
                            }
                          }}
                          disabled={isViewMode}
                        />
                      </Grid.Column>
                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        <InputNumber
                          control={control}
                          errors={errorsFacilityEnergy.Price}
                          labelName={"Price per kWh"}
                          placeholder="Price per kWh"
                          name="Price"
                          required={false}
                          disabled={isViewMode}
                        />
                      </Grid.Column>

                      <Grid.Column computer={4} tablet={8} mobile={16}>
                        {enableEdit ? (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            title="Update"
                            onClick={handleSubmitFacilityEnergy((data: any) => {
                              if (!checkValidData(data)) {
                                addFacilityEnergyData(data);
                              }
                            })}
                            disabled={isViewMode}
                          />
                        ) : (
                          <CustomButton
                            theme="green"
                            customColumnStyle={"addUnitSize"}
                            icon="plus"
                            onClick={handleSubmitFacilityEnergy((data: any) => {
                              if (!checkValidData(data)) {
                                addFacilityEnergyData(data);
                              }
                            })}
                            disabled={isViewMode}
                          />
                        )}
                      </Grid.Column>
                    </Grid>
                  ) : null}

                  <Grid.Column
                    computer={16}
                    tablet={16}
                    mobile={16}
                    className={!renderTableData ? "productRegisterTable customTableBottom" : ""}
                  >
                    {currentFacilityData?.length > 0 && (
                      <CommonTable tableHeaderData={FacilityEnergyTable}>
                        {loadTableData()}
                      </CommonTable>
                    )}
                  </Grid.Column>
                </div>
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </AccordionContent>
      </Accordion>
    </>
  );
};

export default FacilityEnergySimulation;
